import * as React from "react"
import { Head, router } from "@inertiajs/react"
import { uploadFilesSequentially } from "@/lib/uploadFiles"
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Ban,
  CheckCircle2,
  Clock,
  FileSpreadsheet,
  FolderOpen,
  Loader2,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { MonthlyUploadAlert } from "@/components/MonthlyUploadAlert"
import {
  DataCard,
  DataCardActions,
  DataCardField,
  DataCardGrid,
  DataCardHeader,
  DataCardStatus,
  DataCardTitle,
} from "@/components/ui/data-card"
import { MobileFilterSheet } from "@/components/ui/mobile-filter-sheet"
import { MobileFilterSortBar } from "@/components/ui/mobile-filter-sort-bar"
import { MobileSortSheet } from "@/components/ui/mobile-sort-sheet"
import { useMobileFilterSort } from "@/hooks/use-mobile-filter-sort"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { consumer } from "@/lib/actioncable"
import {
  type ColumnDef,
  type DatasetConfig,
  type FilterDef,
  type PreviewRow,
  type UploadRow,
  type UploadStatus,
  type WorkerResult,
  buildNavigateParams,
  formatColumn,
  formatValue,
  initialCheckedFilenames,
  resolveSortLabel,
  toMetadataList,
  MONTHS,
  MONTHS_FULL,
  STATUS_VALUES,
} from "@/lib/datasetRegistry"
import { TERMINAL, computeVisibleUploads } from "./visibleUploads"

// ---------------------------------------------------------------------------
// The single parameterized upload page. One `config` (from datasetRegistry.ts)
// drives every per-dataset difference; the heavy M4 pages pass the typed
// render-prop slots (`headerBanner`, `dropzoneDetail`, `renderPreviewItem`).
//
// SSR-safe: `consumer` is imported at module top-level (like the legacy pages),
// but `subscriptions.create` and every `document`/`window` access happen only
// inside effects / event handlers — never during render.
// ---------------------------------------------------------------------------

type Filters = Record<string, string | null>

type TrackedUpload = {
  id: number
  filename: string
  subtitle: string
  status: UploadStatus
  row_count: number | null
  error_message: string | null
  progress_rows: number
}

type StatusUpdate = {
  type: "status_update"
  upload_id: number
  status: UploadStatus
  row_count?: number | null
  error_message: string | null
  [key: string]: unknown
}

type ProgressUpdate = {
  type: "progress_update"
  upload_id: number
  progress_rows: number
}

export type DatasetUploadsPageProps = {
  config: DatasetConfig
  uploads: UploadRow[]
  /** Full active set across pages — only for `config.inFlightFromProps` (bp_opsc). */
  in_flight_uploads?: UploadRow[]
  total: number
  page: number
  per_page: number
  sort: string
  direction: "asc" | "desc"
  filters: Filters
  available_years?: number[]
  available_regions?: string[]
  // ---- M4 slots (typed + documented; unused by the pilots) ----------------
  /** Rendered above the header (e.g. the timeseries Data-Integrity banner). */
  headerBanner?: React.ReactNode
  /** Replaces the default "sheet/A1" detail line in the drop zone. */
  dropzoneDetail?: React.ReactNode
  /** Replaces the default config-driven preview card for one preview row. */
  renderPreviewItem?: (preview: PreviewRow, checked: boolean, onToggle: () => void) => React.ReactNode
  /** Read-only panel rendered just above the "Riwayat Upload" history table
   *  (Feature #49 promo_activity archive stats). Stat-strip / cards only. */
  archivePanel?: React.ReactNode
}

export function DatasetUploadsPage({
  config,
  uploads,
  in_flight_uploads,
  total,
  page,
  per_page,
  sort,
  direction,
  filters,
  available_years,
  available_regions,
  headerBanner,
  dropzoneDetail,
  renderPreviewItem,
  archivePanel,
  ...rest
}: DatasetUploadsPageProps) {
  const panelMode = config.features.inFlightPanel
  const { basePath, channel } = config

  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const folderInputRef = React.useRef<HTMLInputElement>(null)
  const activeXhrRef = React.useRef<XMLHttpRequest | null>(null)
  const importAbortedRef = React.useRef(false)
  const serverAbortRef = React.useRef<AbortController | null>(null)
  const cancelledRef = React.useRef(false)

  const [previews, setPreviews] = React.useState<PreviewRow[] | null>(null)
  const [workerProgress, setWorkerProgress] = React.useState<{ total: number; done: number } | null>(null)
  const [serverQuerying, setServerQuerying] = React.useState(false)
  const [importFiles, setImportFiles] = React.useState<File[]>([])
  const [importing, setImporting] = React.useState(false)
  const [uploadProgress, setUploadProgress] = React.useState<number | null>(null)
  const [dragOver, setDragOver] = React.useState(false)
  const [checkedFiles, setCheckedFiles] = React.useState<Set<string>>(new Set())
  const [trackedUploads, setTrackedUploads] = React.useState<TrackedUpload[]>([])
  const [searchValue, setSearchValue] = React.useState(filters.search ?? "")
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set())

  const filterKeys = React.useMemo(() => config.filters.map((f) => f.key), [config])

  // Sync search input when URL changes (browser back/forward)
  React.useEffect(() => {
    setSearchValue(filters.search ?? "")
  }, [filters.search])

  // Subscribe to ActionCable for uploads in the in-flight progress panel
  React.useEffect(() => {
    if (trackedUploads.length === 0) return
    const subs = trackedUploads.map((u) =>
      consumer.subscriptions.create(
        { channel, upload_id: u.id },
        {
          received(data: StatusUpdate | ProgressUpdate) {
            setTrackedUploads((prev) =>
              prev.map((t) => {
                if (t.id !== data.upload_id) return t
                if (data.type === "progress_update") {
                  return { ...t, progress_rows: data.progress_rows }
                }
                return {
                  ...t,
                  status: data.status,
                  row_count: data.row_count ?? t.row_count,
                  error_message: data.error_message ?? t.error_message,
                }
              }),
            )
          },
        },
      ),
    )
    return () => subs.forEach((s) => s.unsubscribe())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedUploads.map((u) => u.id).join(","), channel])

  // Live-update history rows for in-flight uploads visible in the table
  const [liveUploads, setLiveUploads] = React.useState<UploadRow[]>(uploads)
  React.useEffect(() => {
    setLiveUploads(uploads)
  }, [uploads])

  // Prop-sourced in-flight panel ("Sedang Diproses", bp_opsc model): the full
  // active set across pages, with per-row progress and drop-on-terminal reload.
  const [liveInFlight, setLiveInFlight] = React.useState<UploadRow[]>(in_flight_uploads ?? [])
  const [progressMap, setProgressMap] = React.useState<Record<number, number>>({})
  React.useEffect(() => {
    setLiveInFlight(in_flight_uploads ?? [])
  }, [in_flight_uploads])

  const liveInFlightKey = React.useMemo(
    () =>
      (config.inFlightFromProps ? liveInFlight : liveUploads.filter((u) => u.status === "pending" || u.status === "processing"))
        .map((u) => u.id)
        .sort((a, b) => a - b)
        .join(","),
    [liveUploads, liveInFlight, config.inFlightFromProps],
  )

  React.useEffect(() => {
    if (!liveInFlightKey) return
    const fromProps = config.inFlightFromProps
    const ids = liveInFlightKey.split(",").map((s) => parseInt(s, 10))
    const subs = ids.map((id) =>
      consumer.subscriptions.create(
        { channel, upload_id: id },
        {
          received(data: StatusUpdate | ProgressUpdate) {
            if (data.type === "progress_update") {
              if (fromProps) setProgressMap((prev) => ({ ...prev, [data.upload_id]: data.progress_rows }))
              return
            }
            setLiveUploads((prev) =>
              prev.map((u) => (u.id === data.upload_id ? applyStatusUpdate(u, data, config.liveFields) : u)),
            )
            if (!fromProps) return
            if (TERMINAL.includes(data.status)) {
              setLiveInFlight((prev) => prev.filter((u) => u.id !== data.upload_id))
              router.reload({ only: ["uploads", "in_flight_uploads"] })
            } else {
              setLiveInFlight((prev) =>
                prev.map((u) => (u.id === data.upload_id ? applyStatusUpdate(u, data, config.liveFields) : u)),
              )
            }
          },
        },
      ),
    )
    return () => subs.forEach((s) => s.unsubscribe())
  }, [liveInFlightKey, channel, config.liveFields, config.inFlightFromProps])

  // -------------------------------------------------------------------------
  // Navigation helper (URL-based filter/sort/page state)
  // -------------------------------------------------------------------------

  function navigate(overrides: Record<string, string | number | null>) {
    const params = buildNavigateParams(filterKeys, filters, sort, direction, page, overrides)
    if (config.features.bulkDelete) setSelectedIds(new Set())
    router.get(basePath, params as Record<string, string>, {
      ...(config.navigatePreserveState ? { preserveState: true } : {}),
      preserveScroll: false,
    })
  }

  function handleSortColumn(col: string) {
    if (sort === col) {
      navigate({ direction: direction === "asc" ? "desc" : "asc", page: null })
    } else {
      navigate({ sort: col, direction: "asc", page: null })
    }
  }

  // -------------------------------------------------------------------------
  // Bulk selection / delete (flag-gated — heavy pages only)
  // -------------------------------------------------------------------------

  // Bug B1: exclude only *in-flight* tracked uploads from the history; a tracked
  // upload that has reached a terminal state must appear in "Riwayat Upload"
  // immediately, without "Upload lagi". See ./visibleUploads.
  const visibleUploads = React.useMemo(
    () => computeVisibleUploads(liveUploads, trackedUploads, panelMode),
    [liveUploads, trackedUploads, panelMode],
  )

  const deletableUploads = React.useMemo(
    () => visibleUploads.filter((u) => u.status !== "pending" && u.status !== "processing"),
    [visibleUploads],
  )

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    if (selectedIds.size === deletableUploads.length && deletableUploads.length > 0) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(deletableUploads.map((u) => u.id)))
    }
  }

  function deleteParams(): Record<string, string> {
    const params: Record<string, string> = {}
    for (const k of filterKeys) {
      if (filters[k]) params[k] = filters[k] as string
    }
    if (sort !== "created_at") params.sort = sort
    if (direction !== "desc") params.direction = direction
    return params
  }

  function handleBulkDelete() {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    const confirmMsg = config.bulkDestroyConfirm?.(ids.length) ?? `Hapus ${ids.length} upload terpilih?`
    if (!window.confirm(confirmMsg)) return
    const data: Record<string, string | number | (string | number)[]> = { ids, ...deleteParams() }
    if (page > 1) data.page = page
    router.delete(`${basePath}/bulk_destroy`, {
      data,
      onFinish: () => setSelectedIds(new Set()),
    })
  }

  function handleDeleteRow(upload: UploadRow) {
    const confirmMsg = config.destroyConfirm?.(upload.filename) ?? `Hapus "${upload.filename}"?`
    if (!window.confirm(confirmMsg)) return
    const data: Record<string, string | number> = deleteParams()
    if (config.destroyKeepsPage && page > 1) data.page = page
    router.delete(`${basePath}/${upload.id}`, {
      ...(config.destroyPreserveScroll ? { preserveScroll: true } : {}),
      data,
    })
  }

  // -------------------------------------------------------------------------
  // Upload handlers
  // -------------------------------------------------------------------------

  function handleFilesSelected(files: FileList | null) {
    if (!files || files.length === 0) return
    const exts = config.accept.split(",")
    const acceptable = Array.from(files).filter((f) => exts.some((ext) => f.name.endsWith(ext)))
    if (acceptable.length === 0) return
    startWorkerPreview(acceptable)
  }

  async function startWorkerPreview(files: File[]) {
    cancelledRef.current = false
    setImportFiles(files)
    setPreviews(null)
    setWorkerProgress({ total: files.length, done: 0 })

    const collected: WorkerResult[] = []
    for (const file of files) {
      if (cancelledRef.current) return
      try {
        const result = await config.preview.parse(file)
        collected.push({ filename: file.name, ...result })
      } catch (err) {
        collected.push({
          filename: file.name,
          error: err instanceof Error ? err.message : "Gagal membaca file",
        })
      }
      if (cancelledRef.current) return
      setWorkerProgress((prev) => (prev ? { ...prev, done: prev.done + 1 } : null))
    }

    if (!cancelledRef.current) await runServerPreview(collected, files)
  }

  async function runServerPreview(workerResults: WorkerResult[], originalFiles: File[]) {
    setWorkerProgress(null)
    setServerQuerying(true)

    const abortController = new AbortController()
    serverAbortRef.current = abortController

    try {
      const metadata = toMetadataList(config.preview.toMetadata, workerResults, config.preview.errorMetadata)

      const resp = await fetch(`${basePath}/preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({ files_metadata: metadata }),
        signal: abortController.signal,
      })

      if (cancelledRef.current) return
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}))
        throw new Error((err as { error?: string }).error ?? `HTTP ${resp.status}`)
      }

      const serverResults: PreviewRow[] = await resp.json()
      const serverMap = new Map(serverResults.map((p) => [p.filename, p]))

      const derive = config.preview.derive
      const finalPreviews: PreviewRow[] = workerResults.map((r) => {
        if (r.error) return { filename: r.filename, error: r.error }
        const preview = serverMap.get(r.filename)
        if (!preview) return { filename: r.filename, error: "Tidak ada hasil dari server" }
        return derive && !preview.error ? derive(preview) : preview
      })

      if (cancelledRef.current) return
      setPreviews(finalPreviews)

      // New files checked by default; duplicates unchecked by default.
      setCheckedFiles(initialCheckedFilenames(finalPreviews))
    } catch (err) {
      if (cancelledRef.current || (err instanceof DOMException && err.name === "AbortError")) return
      setPreviews(originalFiles.map((f) => ({ filename: f.name, error: "Network error" })))
    } finally {
      serverAbortRef.current = null
      if (!cancelledRef.current) setServerQuerying(false)
    }
  }

  function handleCancelPreviewInProgress() {
    cancelledRef.current = true
    serverAbortRef.current?.abort()
    serverAbortRef.current = null
    setWorkerProgress(null)
    setServerQuerying(false)
    setImportFiles([])
    resetFileInputs()
  }

  function handleAbortImport() {
    importAbortedRef.current = true
    activeXhrRef.current?.abort()
    activeXhrRef.current = null
    setImporting(false)
    setUploadProgress(null)
  }

  function handleCancelPreview() {
    setPreviews(null)
    setImportFiles([])
    setCheckedFiles(new Set())
    resetFileInputs()
  }

  async function handleConfirmImport() {
    const filesToImport = importFiles.filter((f) => checkedFiles.has(f.name))
    if (!filesToImport.length) return
    setImporting(true)
    setUploadProgress(0)
    importAbortedRef.current = false

    // Build initial tracked uploads — period info from preview.
    const previewMap = new Map(
      (previews ?? []).filter((p) => !p.error).map((p) => [p.filename, p]),
    )

    try {
      const { uploaded, errors, aborted } = await uploadFilesSequentially<{ queued: number; upload_ids: number[] }>({
        url: basePath,
        files: filesToImport,
        abortRef: importAbortedRef,
        xhrRef: activeXhrRef,
        onProgress: setUploadProgress,
      })
      if (aborted) return

      if (errors.length) alert(`Sebagian file gagal diunggah:\n${errors.join("\n")}`)

      setPreviews(null)
      setImportFiles([])
      setCheckedFiles(new Set())
      setUploadProgress(null)

      if (panelMode) {
        const initial: TrackedUpload[] = uploaded.flatMap(({ file, data }) => {
          const id = data.upload_ids[0]
          if (id == null) return []
          const preview = previewMap.get(file.name)
          return [
            {
              id,
              filename: file.name,
              subtitle: (preview?.[config.trackedSubtitleField ?? "period_label"] as string) ?? "",
              status: "pending" as UploadStatus,
              row_count: null,
              error_message: null,
              progress_rows: 0,
            },
          ]
        })
        setTrackedUploads(initial)
      }

      resetFileInputs()
      router.reload({ only: config.inFlightFromProps ? ["uploads", "in_flight_uploads"] : ["uploads"] })
    } finally {
      setImporting(false)
      setUploadProgress(null)
    }
  }

  async function handleCancelUpload(id: number) {
    await fetch(`${basePath}/${id}/cancel`, {
      method: "PATCH",
      headers: { "X-CSRF-Token": getCsrfToken() },
    })
  }

  function handleUploadAgain() {
    setTrackedUploads([])
    router.reload({ only: ["uploads"] })
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  function toggleFile(filename: string) {
    setCheckedFiles((prev) => {
      const next = new Set(prev)
      if (next.has(filename)) next.delete(filename)
      else next.add(filename)
      return next
    })
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    handleFilesSelected(e.dataTransfer.files)
  }

  function resetFileInputs() {
    if (fileInputRef.current) fileInputRef.current.value = ""
    if (folderInputRef.current) folderInputRef.current.value = ""
  }

  // -------------------------------------------------------------------------
  // Computed
  // -------------------------------------------------------------------------

  const noneChecked = checkedFiles.size === 0
  const busy = !!workerProgress || serverQuerying || importing
  const isInProgressView = panelMode && trackedUploads.length > 0
  const isIdleView = !isInProgressView && !previews && !workerProgress && !serverQuerying

  const allDone = isInProgressView && trackedUploads.every((u) => TERMINAL.includes(u.status))
  const successCount = trackedUploads.filter((u) => u.status === "completed").length
  const cancelCount = trackedUploads.filter((u) => u.status === "cancelled").length
  const failCount = trackedUploads.filter((u) => u.status === "failed").length

  const hasActiveFilter = config.filters.some((f) => !!filters[f.key])

  // Mobile filter / sort sheets
  const { filterOpen, setFilterOpen, sortOpen, setSortOpen, activeFilterCount, applyFilters, resetFilters } =
    useMobileFilterSort(filters, navigate, filterKeys)
  const sortLabel = resolveSortLabel(config.sortOptions, sort, direction)
  const totalPages = Math.ceil(total / per_page)

  // Each `select`/`year` filter draws its options from an `available_*` page prop.
  // available_years/available_regions are named props; anything else (e.g.
  // available_dist_names, or a dot path like "filter_options.regions") arrives
  // via `rest` and is resolved by key here.
  const optionSources: Record<string, (string | number)[]> = {
    available_years: available_years ?? [],
    available_regions: available_regions ?? [],
  }
  for (const f of config.filters) {
    const key = f.kind === "year" ? "available_years" : f.optionsFrom
    if (key && optionSources[key] === undefined) {
      let value: unknown = rest as Record<string, unknown>
      for (const part of key.split(".")) {
        value = (value as Record<string, unknown> | undefined)?.[part]
      }
      optionSources[key] = (value as (string | number)[] | undefined) ?? []
    }
  }

  const { bulkDelete, destroy } = config.features

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <>
      <Head title={config.head.title}>
        <meta name="description" content={config.head.description} />
        <meta property="og:title" content={config.head.title} />
        <meta property="og:description" content={config.head.description} />
      </Head>
      <AppShell width={config.shellWidth}>
        {headerBanner}

        {/* Header */}
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>{config.label}</h1>
              <p className={config.intro.className ?? "mt-1"}>
                {config.intro.prefix}
                {config.intro.strong ? <strong>{config.intro.strong}</strong> : null}
                {config.intro.suffix}
              </p>
            </div>
            {(config.headerButtonsAlwaysVisible || isIdleView) && (
              <>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={config.headerButtonsAlwaysVisible ? busy : importing}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Upload File
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => folderInputRef.current?.click()}
                    disabled={config.headerButtonsAlwaysVisible ? busy : importing}
                  >
                    <FolderOpen className="mr-2 h-4 w-4" />
                    Pilih Folder
                  </Button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={config.accept}
                  multiple
                  className="hidden"
                  onChange={(e) => handleFilesSelected(e.target.files)}
                />
                <input
                  ref={folderInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFilesSelected(e.target.files)}
                  {...({ webkitdirectory: "", mozdirectory: "" } as React.InputHTMLAttributes<HTMLInputElement>)}
                />
              </>
            )}
          </div>
        </div>

        {config.monthlyKey && <MonthlyUploadAlert categoryKey={config.monthlyKey} label={config.label} />}

        {/* Drop zone */}
        {isIdleView && (
          <div
            onDragOver={(e) => {
              e.preventDefault()
              setDragOver(true)
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={[
              "mt-6 flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors",
              dragOver ? "border-accent bg-surface" : "border-hairline",
            ].join(" ")}
          >
            <FileSpreadsheet className="mb-3 h-10 w-10 text-ink-muted" />
            <p className="text-sm font-medium text-ink-display">{config.dropzone.dragLine}</p>
            {config.dropzone.formatCode && (
              <p className="mt-1 text-xs text-ink-muted">
                Format: <code>{config.dropzone.formatCode}</code>{config.dropzone.formatSuffix}
              </p>
            )}
            {dropzoneDetail ??
              (config.dropzone.periodFromA1 === false ? (
                <p className="mt-1 text-xs text-ink-muted">
                  Sheet yang dibaca: <strong>{config.dropzone.sheetName}</strong>
                </p>
              ) : (
                <p className="mt-1 text-xs text-ink-muted">
                  Sheet yang dibaca: <strong>{config.dropzone.sheetName}</strong> · Periode dibaca dari sel judul{" "}
                  <strong>A1</strong> di dalam file.
                </p>
              ))}
          </div>
        )}

        {/* Worker progress */}
        {workerProgress && (
          <div className="mt-8 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-ink-muted">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">
                  {config.workerProgressLabel ?? "Membaca file…"} {workerProgress.done}/{workerProgress.total}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleCancelPreviewInProgress}>
                <Ban className="mr-1 h-3 w-3" />
                Batal
              </Button>
            </div>
            <ProgressBar value={Math.round((workerProgress.done / workerProgress.total) * 100)} />
          </div>
        )}

        {/* Server querying */}
        {serverQuerying &&
          (config.serverQueryingInline ? (
            <div className="mt-8 flex items-center gap-2 text-ink-muted">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">{config.serverQueryingLabel ?? "Mengambil info duplikat…"}</span>
            </div>
          ) : (
            <div className="mt-8 flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 text-ink-muted">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">{config.serverQueryingLabel ?? "Mengambil info duplikat…"}</span>
              </div>
            </div>
          ))}

        {/* Preview panel */}
        {!isInProgressView && previews && !serverQuerying && !(config.previewHiddenWhileImporting && importing) && (
          <div className="mt-6">
            <h2 className={config.headingClass ? `mb-1 ${config.headingClass}` : "mb-1"}>Preview Import</h2>
            <p className="mb-4 text-sm text-ink-muted">Periksa detail berikut sebelum memulai import.</p>
            <div className="space-y-3">
              {previews.map((p) =>
                p.error ? (
                  <PreviewErrorCard
                    key={p.filename}
                    filename={p.filename}
                    error={p.error}
                    badge={config.preview.errorBadge}
                    detail={config.preview.errorDetail}
                  />
                ) : renderPreviewItem ? (
                  <React.Fragment key={p.filename}>
                    {renderPreviewItem(p, checkedFiles.has(p.filename), () => toggleFile(p.filename))}
                  </React.Fragment>
                ) : (
                  <PreviewCard
                    key={p.filename}
                    config={config}
                    preview={p}
                    checked={checkedFiles.has(p.filename)}
                    onToggle={() => toggleFile(p.filename)}
                  />
                ),
              )}
            </div>

            {importing && uploadProgress !== null && (
              <div className="mt-4 space-y-1.5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-ink-muted">
                    {uploadProgress < 100 ? `Mengirim file… ${uploadProgress}%` : "File terkirim, menunggu antrian…"}
                  </p>
                  {uploadProgress < 100 && (
                    <Button variant="ghost" size="sm" onClick={handleAbortImport}>
                      <Ban className="mr-1 h-3 w-3" />
                      Batal
                    </Button>
                  )}
                </div>
                <ProgressBar value={uploadProgress} />
              </div>
            )}

            <div className="mt-5 flex gap-3">
              <Button variant="primary" onClick={handleConfirmImport} disabled={importing || noneChecked}>
                {importing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {uploadProgress !== null && uploadProgress < 100 ? `Mengirim… ${uploadProgress}%` : "Mengantri…"}
                  </>
                ) : (
                  "Konfirmasi Import"
                )}
              </Button>
              <Button variant="secondary" onClick={handleCancelPreview} disabled={importing}>
                Batal
              </Button>
            </div>
          </div>
        )}

        {/* Progress view (tracked in-flight panel) */}
        {isInProgressView && (
          <div className="mt-6">
            <h2 className={config.headingClass ? `mb-1 ${config.headingClass}` : "mb-1"}>Progress Import</h2>
            <p className="mb-4 text-sm text-ink-muted">Status tiap file diperbarui secara real-time.</p>

            <div className="space-y-3">
              {trackedUploads.map((u) => (
                <ProgressCard key={u.id} upload={u} rowNoun={config.rowNoun ?? "baris"} onCancel={() => handleCancelUpload(u.id)} />
              ))}
            </div>

            {allDone && (
              <div className="mt-5 rounded-md border border-hairline bg-surface p-4">
                <p className="text-sm font-medium text-ink-display">
                  {successCount > 0 && (
                    <span className="mr-3 text-green-600 dark:text-green-400">{successCount} berhasil</span>
                  )}
                  {cancelCount > 0 && <span className="mr-3 text-ink-muted">{cancelCount} dibatalkan</span>}
                  {failCount > 0 && <span className="text-red-600 dark:text-red-400">{failCount} gagal</span>}
                </p>
                <Button variant="secondary" className="mt-3" onClick={handleUploadAgain}>
                  Upload lagi
                </Button>
              </div>
            )}
          </div>
        )}

        {/* In-flight panel sourced from the in_flight_uploads prop (bp_opsc model) */}
        {config.inFlightFromProps && liveInFlight.length > 0 && (
          <div className="mt-10">
            <div className="mb-4 flex items-center gap-2">
              <h2 className="mb-0">Sedang Diproses</h2>
              <Badge tone="accent">{liveInFlight.length} sedang diproses</Badge>
            </div>
            <div className="space-y-2">
              {liveInFlight.map((u) => (
                <InFlightRow
                  key={u.id}
                  config={config}
                  upload={u}
                  progressRows={progressMap[u.id]}
                  onCancel={() => handleCancelUpload(u.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Read-only archive panel (Feature #49 promo_activity), above the history. */}
        {archivePanel}

        {/* History table */}
        <div className="mt-10">
          {config.showHistoryHeading !== false && (
            <h2 className={config.headingClass ? `mb-4 ${config.headingClass}` : "mb-4"}>Riwayat Upload</h2>
          )}

          {/* Filter + Sort bar (mobile) */}
          <div className="mb-4 md:hidden">
            <MobileFilterSortBar
              filterCount={activeFilterCount}
              sortLabel={sortLabel}
              onFilterClick={() => setFilterOpen(true)}
              onSortClick={() => setSortOpen(true)}
            />
          </div>

          {/* Filter bar (desktop) */}
          <div className="mb-4 hidden flex-wrap items-end gap-3 md:flex">
            {config.filters.map((f) => (
              <DesktopFilter
                key={f.key}
                filter={f}
                value={filters[f.key] ?? ""}
                searchValue={searchValue}
                onSearchChange={setSearchValue}
                optionSources={optionSources}
                navigate={navigate}
              />
            ))}

            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate({ ...Object.fromEntries(filterKeys.map((k) => [k, null])), page: null })
                }
              >
                Reset filter
              </Button>
            )}
          </div>

          {/* Bulk action bar */}
          {bulkDelete && selectedIds.size > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-hairline bg-surface px-3 py-2">
              <span className="text-xs text-ink-muted">{selectedIds.size} {config.bulkSelectedNoun ?? (config.tableStyle === "accent" ? "terpilih" : "dipilih")}</span>
              <Button variant="ghost" size="sm" type="button" onClick={handleBulkDelete}>
                <Trash2 className="mr-1 h-3 w-3" />
                Hapus terpilih
              </Button>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                className="ml-auto"
                onClick={() => setSelectedIds(new Set())}
              >
                Batal pilih
              </Button>
            </div>
          )}

          {visibleUploads.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {hasActiveFilter ? "Tidak ada upload yang cocok dengan filter." : "Belum ada upload."}
            </p>
          ) : (
            <>
              {/* Mobile card list */}
              <div className="space-y-3 md:hidden">
                {visibleUploads.map((u) => (
                  <UploadCard
                    key={u.id}
                    config={config}
                    upload={u}
                    selected={selectedIds.has(u.id)}
                    onToggleSelect={() => toggleSelect(u.id)}
                    onCancel={() => handleCancelUpload(u.id)}
                    onDelete={() => handleDeleteRow(u)}
                  />
                ))}
              </div>

              {/* Desktop table */}
              <div className={`hidden ${config.tableScrollX ? "overflow-x-auto" : "overflow-hidden"} rounded-md border border-hairline md:block`}>
                <table className="w-full text-sm">
                  <thead className="bg-surface">
                    <tr>
                      {bulkDelete && (
                        <th className="w-10 px-3 py-2.5">
                          <Checkbox
                            checked={deletableUploads.length > 0 && selectedIds.size === deletableUploads.length}
                            onChange={toggleSelectAll}
                            aria-label="Pilih semua"
                            disabled={deletableUploads.length === 0}
                          />
                        </th>
                      )}
                      {config.columns.map((col) =>
                        col.role === "title" ? (
                          <th key={col.key} className="px-4 py-2.5 text-left font-medium text-ink-muted">
                            {col.label}
                          </th>
                        ) : col.sortable ? (
                          <SortableHeader
                            key={col.key}
                            col={col.key}
                            label={col.label}
                            align={col.align}
                            sort={sort}
                            direction={direction}
                            onSort={handleSortColumn}
                            accent={config.tableStyle === "accent"}
                          />
                        ) : (
                          <th
                            key={col.key}
                            className={`px-4 py-2.5 font-medium text-ink-muted text-${col.align ?? "left"}`}
                          >
                            {col.label}
                          </th>
                        ),
                      )}
                      {destroy && <th className={config.destroyColumn?.thClass ?? "w-10 px-2 py-2.5"} />}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-hairline">
                    {visibleUploads.map((u) => (
                      <UploadTableRow
                        key={u.id}
                        config={config}
                        upload={u}
                        selected={selectedIds.has(u.id)}
                        onToggleSelect={() => toggleSelect(u.id)}
                        onCancel={() => handleCancelUpload(u.id)}
                        onDelete={() => handleDeleteRow(u)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Summary + pagination */}
          {total > 0 && (
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-ink-muted">
                Menampilkan {(page - 1) * per_page + 1}–{Math.min(page * per_page, total)} dari {total} upload
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => navigate({ page: page - 1 })}>
                    Sebelumnya
                  </Button>
                  <span className="text-sm text-ink-muted">
                    Hal. {page} / {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => navigate({ page: page + 1 })}
                  >
                    Berikutnya
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile filter sheet */}
        <MobileFilterSheet
          open={filterOpen}
          onOpenChange={setFilterOpen}
          initial={Object.fromEntries(config.filters.map((f) => [f.key, filters[f.key] ?? ""]))}
          onApply={applyFilters}
          onReset={resetFilters}
        >
          {(draft, setDraft) => (
            <>
              {config.filters.map((f) => (
                <MobileFilter
                  key={f.key}
                  filter={f}
                  value={draft[f.key] ?? ""}
                  onChange={(v) => setDraft({ ...draft, [f.key]: v })}
                  optionSources={optionSources}
                />
              ))}
            </>
          )}
        </MobileFilterSheet>

        {/* Mobile sort sheet */}
        <MobileSortSheet
          open={sortOpen}
          onOpenChange={setSortOpen}
          current={{ sort, direction }}
          options={config.sortOptions}
          onSelect={(opt) => {
            navigate({ sort: opt.sort, direction: opt.direction })
            setSortOpen(false)
          }}
        />
      </AppShell>
    </>
  )
}

// ---------------------------------------------------------------------------
// Status-update merge (history rows)
// ---------------------------------------------------------------------------

function applyStatusUpdate(row: UploadRow, data: StatusUpdate, liveFields: string[]): UploadRow {
  const next: UploadRow = {
    ...row,
    status: data.status,
    error_message: (data.error_message ?? row.error_message) as string | null,
  }
  for (const f of liveFields) {
    if (data[f] != null) next[f] = data[f]
  }
  return next
}

// ---------------------------------------------------------------------------
// Filter controls (desktop bar + mobile sheet) — rendered from a FilterDef
// ---------------------------------------------------------------------------

function DesktopFilter({
  filter,
  value,
  searchValue,
  onSearchChange,
  optionSources,
  navigate,
}: {
  filter: FilterDef
  value: string
  searchValue: string
  onSearchChange: (v: string) => void
  optionSources: Record<string, (string | number)[]>
  navigate: (overrides: Record<string, string | number | null>) => void
}) {
  if (filter.kind === "search") {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault()
          navigate({ [filter.key]: searchValue || null, page: null })
        }}
      >
        <Input
          type="search"
          placeholder={filter.placeholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onBlur={() => navigate({ [filter.key]: searchValue || null, page: null })}
          className={filter.width}
        />
      </form>
    )
  }

  return (
    <Select
      value={value}
      onChange={(e) => navigate({ [filter.key]: e.target.value || null, page: null })}
      className={filter.width}
    >
      <option value="">{filter.allLabel}</option>
      <FilterOptions filter={filter} optionSources={optionSources} />
    </Select>
  )
}

function MobileFilter({
  filter,
  value,
  onChange,
  optionSources,
}: {
  filter: FilterDef
  value: string
  onChange: (v: string) => void
  optionSources: Record<string, (string | number)[]>
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-ink-display">{filter.label}</span>
      {filter.kind === "search" ? (
        <Input type="search" value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <Select value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">{filter.allLabel}</option>
          <FilterOptions filter={filter} optionSources={optionSources} mobile />
        </Select>
      )}
    </label>
  )
}

function FilterOptions({
  filter,
  optionSources,
  mobile = false,
}: {
  filter: FilterDef
  optionSources: Record<string, (string | number)[]>
  mobile?: boolean
}) {
  switch (filter.kind) {
    case "month":
      return (
        <>
          {MONTHS.slice(1).map((name, i) => (
            <option key={i + 1} value={mobile ? String(i + 1) : i + 1}>
              {name}
            </option>
          ))}
        </>
      )
    case "status":
      return (
        <>
          {STATUS_VALUES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </>
      )
    case "year":
    case "select": {
      const source = filter.kind === "year" ? "available_years" : filter.optionsFrom
      const options = (source && optionSources[source]) || []
      return (
        <>
          {options.map((o) => (
            <option key={o} value={o} className={filter.capitalizeOptions ? "capitalize" : undefined}>
              {filter.monthNameLabels ? MONTHS_FULL[Number(o)] : o}
            </option>
          ))}
        </>
      )
    }
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// Table / card cell rendering
// ---------------------------------------------------------------------------

function SortableHeader({
  col,
  label,
  sort,
  direction,
  onSort,
  align = "left",
  accent = false,
}: {
  col: string
  label: string
  sort: string
  direction: "asc" | "desc"
  onSort: (col: string) => void
  align?: "left" | "right"
  accent?: boolean
}) {
  const active = sort === col
  const arrow = accent ? "h-3 w-3 text-accent" : "h-3 w-3"
  // The accent variant mirrors the legacy template literal exactly, including the
  // trailing space on left-aligned headers (`...gap-1 ` + "").
  const spanClass = accent
    ? `inline-flex items-center gap-1 ${align === "right" ? "justify-end w-full" : ""}`
    : "inline-flex items-center gap-1"
  return (
    <th
      className={`cursor-pointer select-none px-4 py-2.5 font-medium text-ink-muted hover:text-ink-body text-${align}`}
      onClick={() => onSort(col)}
    >
      <span className={spanClass}>
        {label}
        {active ? (
          direction === "asc" ? (
            <ArrowUp className={arrow} />
          ) : (
            <ArrowDown className={arrow} />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </span>
    </th>
  )
}

function cellClassName(col: ColumnDef): string {
  if (col.format === "date-or-created") return "px-4 py-3 text-xs text-ink-muted"
  if (col.align === "right") return "px-4 py-3 text-right tabular-nums text-ink-body"
  if (col.capitalize) return "px-4 py-3 capitalize text-ink-body"
  return "px-4 py-3 text-ink-body"
}

function UploadTableRow({
  config,
  upload,
  selected,
  onToggleSelect,
  onCancel,
  onDelete,
}: {
  config: DatasetConfig
  upload: UploadRow
  selected: boolean
  onToggleSelect: () => void
  onCancel: () => void
  onDelete: () => void
}) {
  const { bulkDelete, destroy } = config.features
  const accent = config.tableStyle === "accent"
  const isInFlight = upload.status === "pending" || upload.status === "processing"

  return (
    <tr className={accent ? (selected ? "bg-accent/5" : "") : undefined}>
      {bulkDelete && (
        <td className="px-3 py-3">
          {accent ? (
            !isInFlight && (
              <Checkbox checked={selected} onChange={onToggleSelect} aria-label={`Pilih ${upload.filename}`} />
            )
          ) : (
            <Checkbox checked={selected} onChange={onToggleSelect} disabled={isInFlight} aria-label={`Pilih ${upload.filename}`} />
          )}
        </td>
      )}
      {config.columns.map((col) => {
        if (col.role === "title") {
          return (
            <td
              key={col.key}
              className={`${col.maxWidth ?? "max-w-[220px]"} truncate px-4 py-3 font-medium text-ink-display`}
              title={upload.filename}
            >
              {upload.filename}
            </td>
          )
        }
        if (col.role === "status") {
          return (
            <td key={col.key} className="px-4 py-3">
              <StatusBadge status={upload.status} />
            </td>
          )
        }
        if (col.format === "primary-sub") {
          return (
            <td key={col.key} className="px-4 py-3 text-ink-body">
              <div className="font-medium">{formatColumn(col, upload)}</div>
              <div className="text-xs text-ink-muted">{col.subField ? String(upload[col.subField] ?? "") : ""}</div>
            </td>
          )
        }
        return (
          <td key={col.key} className={cellClassName(col)}>
            {formatColumn(col, upload)}
          </td>
        )
      })}
      {destroy &&
        (accent && config.tableCancelInFlight ? (
          // bp_opsc hybrid: accent table, but the plain style's cancel-or-delete
          // action cell (px-2, Ban for in-flight rows, accent-styled delete).
          <td className="px-2 py-3 text-right">
            {isInFlight ? (
              <button
                type="button"
                onClick={onCancel}
                title="Batalkan upload ini"
                className="rounded p-1 text-ink-muted transition-colors hover:text-red-600"
              >
                <Ban className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={onDelete}
                className="rounded p-1 text-ink-muted hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
                title="Hapus upload ini"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </td>
        ) : accent ? (
          <td className={config.destroyColumn?.tdClass ?? "px-4 py-3 text-right"}>
            {!isInFlight && (
              <button
                type="button"
                onClick={onDelete}
                className="rounded p-1 text-ink-muted hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
                title="Hapus upload ini"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </td>
        ) : (
          <td className="px-2 py-3">
            {isInFlight ? (
              <button
                onClick={onCancel}
                title="Batalkan upload ini"
                className="rounded p-1 text-ink-muted transition-colors hover:text-red-600"
              >
                <Ban className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                onClick={onDelete}
                title="Hapus upload ini"
                className="rounded p-1 text-ink-muted transition-colors hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </td>
        ))}
    </tr>
  )
}

function UploadCard({
  config,
  upload,
  selected,
  onToggleSelect,
  onCancel,
  onDelete,
}: {
  config: DatasetConfig
  upload: UploadRow
  selected: boolean
  onToggleSelect: () => void
  onCancel: () => void
  onDelete: () => void
}) {
  const { bulkDelete, destroy } = config.features
  const accent = config.tableStyle === "accent"
  const isInFlight = upload.status === "pending" || upload.status === "processing"
  const fieldColumns = (config.mobileFields ?? config.columns).filter((c) => c.role !== "title" && c.role !== "status")
  const title = (
    <DataCardTitle>
      <span className="break-all" title={upload.filename}>
        {upload.filename}
      </span>
    </DataCardTitle>
  )

  return (
    <DataCard className={accent && selected ? "border-accent/30 bg-accent/5" : undefined}>
      <DataCardHeader>
        {accent ? (
          bulkDelete ? (
            <div className="flex items-center gap-2">
              {!isInFlight && (
                <Checkbox checked={selected} onChange={onToggleSelect} aria-label={`Pilih ${upload.filename}`} />
              )}
              {title}
            </div>
          ) : (
            title
          )
        ) : bulkDelete ? (
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <Checkbox
              checked={selected}
              onChange={onToggleSelect}
              disabled={isInFlight}
              aria-label={`Pilih ${upload.filename}`}
              className="mt-0.5 shrink-0"
            />
            {title}
          </div>
        ) : (
          title
        )}
        <DataCardStatus>
          <StatusBadge status={upload.status} />
        </DataCardStatus>
      </DataCardHeader>
      <DataCardGrid>
        {fieldColumns.map((col) => (
          <DataCardField
            key={col.key}
            wide={col.wide}
            label={col.mobileLabel ?? col.label}
            value={
              col.format === "primary-sub" ? (
                <>
                  <div className="font-medium">{formatColumn(col, upload)}</div>
                  <div className="text-xs text-ink-muted">{col.subField ? String(upload[col.subField] ?? "") : ""}</div>
                </>
              ) : col.capitalize ? (
                <span className="capitalize">{formatColumn(col, upload)}</span>
              ) : (
                formatColumn(col, upload)
              )
            }
          />
        ))}
      </DataCardGrid>
      {destroy &&
        (accent && !config.tableCancelInFlight ? (
          !isInFlight && (
            <DataCardActions>
              <Button variant="ghost" size="sm" onClick={onDelete} className="gap-2 text-red-600">
                <Trash2 className="h-4 w-4" />
                Hapus
              </Button>
            </DataCardActions>
          )
        ) : isInFlight ? (
          <DataCardActions>
            <Button variant="ghost" size="sm" onClick={onCancel} className="gap-2 text-red-600">
              <Ban className="h-4 w-4" />
              Batalkan
            </Button>
          </DataCardActions>
        ) : (
          <DataCardActions>
            <Button variant="ghost" size="sm" onClick={onDelete} className="gap-2 text-red-600">
              <Trash2 className="h-4 w-4" />
              Hapus
            </Button>
          </DataCardActions>
        ))}
    </DataCard>
  )
}

// ---------------------------------------------------------------------------
// Preview cards (config-driven default; override via `renderPreviewItem`)
// ---------------------------------------------------------------------------

function PreviewCard({
  config,
  preview,
  checked,
  onToggle,
}: {
  config: DatasetConfig
  preview: PreviewRow
  checked: boolean
  onToggle: () => void
}) {
  const { metaFields, stats } = config.preview.card
  const isDuplicate = preview.will_replace === true
  const isUnchanged = preview.is_unchanged === true

  return (
    <div
      className={[
        "rounded-md border p-4",
        isDuplicate ? "border-signal bg-signal-faded" : "border-hairline bg-surface",
        !checked ? "opacity-60" : "",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Checkbox id={`check-${preview.filename}`} checked={checked} onChange={onToggle} className="mt-0.5 shrink-0" />
          <label
            htmlFor={`check-${preview.filename}`}
            className="min-w-0 cursor-pointer truncate font-normal text-ink-body"
            title={preview.filename}
          >
            {preview.filename}
          </label>
        </div>
        {isDuplicate && (
          <Badge tone="signal" className="shrink-0">
            Replacement
          </Badge>
        )}
      </div>

      <div className="mt-2 flex flex-wrap gap-4 pl-6 text-xs text-ink-muted">
        {metaFields
          .filter((m) => m.when !== "truthy" || preview[m.field])
          .map((m) => (
            <span key={m.field}>
              {m.label}:{" "}
              <strong className={m.capitalize ? "text-ink-body capitalize" : "text-ink-body"}>
                {formatValue(m.format, preview[m.field])}
              </strong>
            </span>
          ))}
      </div>

      {isDuplicate ? (
        isUnchanged ? (
          <div className="mt-3 pl-6">
            <Badge tone="muted">Tidak ada perubahan terdeteksi</Badge>
          </div>
        ) : (
          // The b2b accent pages flatten the grid wrapper into one div (border +
          // pl-6 combined); the default nests the bordered box in a pl-6 padder.
          <DuplicateGridWrapper flat={config.preview.duplicateGridFlat === true}>
            <div className="grid grid-cols-3 bg-surface px-3 py-1.5 font-medium text-ink-muted">
              <span />
              <span className="text-center">Sebelumnya</span>
              <span className="text-center">File ini</span>
            </div>
            <div className="divide-y divide-hairline">
              {stats.map((stat) => (
                <div key={stat.field} className="grid grid-cols-3 px-3 py-1.5">
                  <span className="text-ink-muted">{stat.label}</span>
                  <span className="text-center tabular-nums text-ink-body">
                    {stat.existingField != null && preview[stat.existingField] != null
                      ? formatValue(stat.format, preview[stat.existingField])
                      : "—"}
                  </span>
                  <span className="text-center tabular-nums text-ink-body">
                    {formatValue(stat.format, preview[stat.field])}
                  </span>
                </div>
              ))}
            </div>
          </DuplicateGridWrapper>
        )
      ) : config.preview.statsInline === false ? null : (
        <div className="mt-2 flex flex-wrap gap-4 pl-6 text-xs text-ink-muted">
          {stats.map((stat) => (
            <span key={stat.field}>
              {stat.inlineLabel ?? stat.label}:{" "}
              <strong className="text-ink-body">{formatValue(stat.format, preview[stat.field])}</strong>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function DuplicateGridWrapper({ flat, children }: { flat: boolean; children: React.ReactNode }) {
  if (flat) {
    return <div className="mt-3 overflow-hidden rounded border border-hairline pl-6 text-xs">{children}</div>
  }
  return (
    <div className="mt-3 pl-6">
      <div className="overflow-hidden rounded border border-hairline text-xs">{children}</div>
    </div>
  )
}

function PreviewErrorCard({
  filename,
  error,
  badge,
  detail,
}: {
  filename: string
  error: string
  badge?: string
  detail?: string
}) {
  return (
    <div className="rounded-md border border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-950">
      <div className="flex items-center gap-2">
        <XCircle className="h-4 w-4 shrink-0 text-red-500" />
        <span className="truncate font-medium text-ink-display">{filename}</span>
        {badge && (
          <Badge tone="danger" className="shrink-0 ml-auto">
            {badge}
          </Badge>
        )}
      </div>
      <p className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</p>
      {detail && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{detail}</p>}
    </div>
  )
}

function ProgressCard({ upload, rowNoun, onCancel }: { upload: TrackedUpload; rowNoun: string; onCancel: () => void }) {
  const isInFlight = upload.status === "pending" || upload.status === "processing"
  const showProgress = upload.status === "processing" && upload.progress_rows > 0

  return (
    <div className="rounded-md border border-hairline bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink-display" title={upload.filename}>
            {upload.filename}
          </p>
          {upload.subtitle && <p className="mt-0.5 text-xs text-ink-muted">{upload.subtitle}</p>}
          {upload.status === "completed" && upload.row_count != null && (
            <p className="mt-0.5 text-xs text-ink-muted">{upload.row_count.toLocaleString()} {rowNoun} diimport</p>
          )}
          {showProgress && (
            <p className="mt-0.5 text-xs text-ink-muted">{upload.progress_rows.toLocaleString()} {rowNoun} diproses…</p>
          )}
          {upload.error_message && (
            <p className="mt-0.5 text-xs text-red-600 dark:text-red-400" title={upload.error_message}>
              {upload.error_message.length > 80 ? upload.error_message.slice(0, 80) + "…" : upload.error_message}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StatusBadge status={upload.status} />
          {isInFlight && (
            <Button variant="secondary" size="sm" onClick={onCancel}>
              <Ban className="mr-1 h-3 w-3" />
              Batalkan
            </Button>
          )}
        </div>
      </div>
      {(showProgress || upload.status === "pending") && (
        <div className="mt-2">
          <ProgressBar indeterminate />
        </div>
      )}
    </div>
  )
}

/** One row of the prop-sourced "Sedang Diproses" panel (bp_opsc model). */
function InFlightRow({
  config,
  upload,
  progressRows,
  onCancel,
}: {
  config: DatasetConfig
  upload: UploadRow
  progressRows: number | undefined
  onCancel: () => void
}) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-hairline bg-surface px-4 py-3">
      <FileSpreadsheet className="h-4 w-4 shrink-0 text-ink-muted" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-ink-display" title={upload.filename}>
          {upload.filename}
        </p>
        <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-ink-muted">
          {(config.inFlightFields ?? []).map((col) => (
            <span key={col.key}>{formatColumn(col, upload)}</span>
          ))}
          {upload.status === "processing" && progressRows != null && (
            <span className="tabular-nums">{progressRows.toLocaleString()} {config.rowNoun ?? "baris"} diproses</span>
          )}
        </div>
      </div>
      <StatusBadge status={upload.status} />
      <button
        type="button"
        onClick={onCancel}
        title="Batalkan upload ini"
        className="rounded p-1 text-ink-muted transition-colors hover:text-red-600"
      >
        <Ban className="h-4 w-4" />
      </button>
    </div>
  )
}

function StatusBadge({ status }: { status: UploadStatus }) {
  switch (status) {
    case "pending":
      return (
        <Badge tone="muted">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      )
    case "processing":
      return (
        <Badge tone="accent">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Processing
        </Badge>
      )
    case "completed":
      return (
        <Badge tone="success">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Completed
        </Badge>
      )
    case "failed":
      return (
        <Badge tone="danger">
          <XCircle className="mr-1 h-3 w-3" />
          Failed
        </Badge>
      )
    case "cancelled":
      return (
        <Badge tone="muted">
          <Ban className="mr-1 h-3 w-3" />
          Dibatalkan
        </Badge>
      )
  }
}

function ProgressBar({ value, indeterminate }: { value?: number; indeterminate?: boolean }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-hairline">
      {indeterminate ? (
        <div className="h-full w-1/3 animate-[progress-slide_1.4s_ease-in-out_infinite] rounded-full bg-accent" />
      ) : (
        <div
          className="h-full rounded-full bg-accent transition-all duration-300"
          style={{ width: `${Math.min(value ?? 0, 100)}%` }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function getCsrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ""
}
